import { useEffect, useRef, useState } from "react";
import {
  loadTurnstileScript,
  TURNSTILE_SITE_KEY,
} from "@/lib/turnstile";

interface TurnstileWidgetProps {
  // Fires with the token on pass, with empty string on fail/expiry. Callers
  // should keep the latest value in state and submit it as `cf-turnstile-response`.
  onChange: (token: string) => void;
}

// Renders a Turnstile checkbox. If no site key is configured (e.g. local dev
// without 1Password), the widget is hidden and the callback fires with an
// empty token immediately — the backend guard passes in non-production when
// no secret is set, so the dev experience stays unbroken.
export const TurnstileWidget = ({ onChange }: TurnstileWidgetProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) {
      // No site key — pass an empty token and let the backend decide.
      onChange("");
      return;
    }
    let mounted = true;
    loadTurnstileScript()
      .then(() => {
        if (!mounted || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => onChange(token),
          "error-callback": () => {
            onChange("");
            setError("Captcha failed. Please retry.");
          },
          "expired-callback": () => {
            onChange("");
            setError("Captcha expired. Please retry.");
          },
        });
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Captcha unavailable");
        onChange("");
      });
    return () => {
      mounted = false;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // best-effort cleanup
        }
      }
    };
  }, [onChange]);

  if (!TURNSTILE_SITE_KEY) return null;
  return (
    <div>
      <div ref={containerRef} />
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
};
